// src/utils/exportSoToWord.js
// ===============================================
// LẤY KHÁCH HÀNG TỪ FIRESTORE + XUẤT HỢP ĐỒNG (DOCX/PDF)
// - Có recalc totals theo items đã chọn
// ===============================================

import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';

// Firebase v9 (modular)
import { getApp } from 'firebase/app';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
} from 'firebase/firestore';

/* ---------------- Helpers ---------------- */
const toDate = (v) => {
  if (!v) return null;
  try { return typeof v?.toDate === 'function' ? v.toDate() : new Date(v); } catch { return null; }
};
const pad2 = (n) => String(n).padStart(2, '0');
const num  = (v) => Number(v ?? 0);
const VND  = (n) => new Intl.NumberFormat('vi-VN', { style:'currency', currency:'VND' }).format(num(n));
const pick = (obj, paths, fallback = '') => {
  for (const path of paths) {
    const val = path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
    if (val !== undefined && val !== null && val !== '') return val;
  }
  return fallback;
};
const getTemplateURL = () => `${window.location.origin}/template.docx`;

/* ---- Number to Vietnamese words ---- */
const VN_DIGITS = ['không','một','hai','ba','bốn','năm','sáu','bảy','tám','chín'];
const VN_UNITS  = ['', 'nghìn', 'triệu', 'tỷ', 'nghìn tỷ', 'triệu tỷ', 'tỷ tỷ'];

function readTripletVN(n, { showZeroHundred = false } = {}) {
  const hundreds = Math.floor(n / 100);
  const tens     = Math.floor((n % 100) / 10);
  const units    = n % 10;
  const parts = [];

  if (hundreds > 0) parts.push(VN_DIGITS[hundreds], 'trăm');
  else if (showZeroHundred) parts.push('không', 'trăm');

  if (tens > 1) {
    parts.push(VN_DIGITS[tens], 'mươi');
    if (units === 1) parts.push('mốt');
    else if (units === 4) parts.push('tư');
    else if (units === 5) parts.push('lăm');
    else if (units > 0) parts.push(VN_DIGITS[units]);
  } else if (tens === 1) {
    parts.push('mười');
    if (units === 5) parts.push('lăm');
    else if (units > 0) parts.push(VN_DIGITS[units]);
  } else {
    if (units > 0) {
      if (hundreds > 0 || showZeroHundred) parts.push('lẻ');
      parts.push(units === 5 && (hundreds > 0 || showZeroHundred) ? 'năm' : VN_DIGITS[units]);
    }
  }

  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

function numberToWordsVI(n) {
  let x = Math.round(Number(n || 0));
  if (x === 0) return 'không';

  let sign = '';
  if (x < 0) { sign = 'âm '; x = Math.abs(x); }

  const groups = [];
  while (x > 0) { groups.push(x % 1000); x = Math.floor(x / 1000); }

  const topIdx = groups.length - 1;

  const words = [];
  for (let i = topIdx; i >= 0; i--) {
    const val = groups[i];
    if (val === 0) continue;

    const hundreds = Math.floor(val / 100);
    const tens     = Math.floor((val % 100) / 10);
    const units    = val % 10;

    const showZeroHundred = (i < topIdx) && (hundreds === 0) && (tens === 0) && (units > 0);
    const chunk = readTripletVN(val, { showZeroHundred });

    if (chunk) {
      words.push(chunk);
      if (VN_UNITS[i]) words.push(VN_UNITS[i]);
    }
  }

  return (sign + words.join(' ').replace(/\s+/g, ' ').trim());
}

function moneyToWordsVI(n, { capitalize = true, addDong = true } = {}) {
  let w = numberToWordsVI(n);
  if (addDong) w += ' đồng' + (Number(n) % 1 === 0 ? ' chẵn' : '');
  if (capitalize && w) w = w.charAt(0).toUpperCase() + w.slice(1);
  return w;
}

/* ---------------- Firestore: lấy khách hàng ---------------- */
async function fetchCustomerFromFirestore(order) {
  try {
    const app = getApp();
    const db = getFirestore(app);

    const byId =
      order?.customerId || (order?.customerRef && (order.customerRef.id || order.customerRef));

    if (byId) {
      const snap = await getDoc(doc(db, 'customers', String(byId)));
      if (snap.exists()) return { id: snap.id, ...snap.data() };
    }

    if (order?.customerCode) {
      const qs = await getDocs(
        query(collection(db, 'customers'), where('code', '==', order.customerCode), limit(1))
      );
      if (!qs.empty) {
        const s = qs.docs[0];
        return { id: s.id, ...s.data() };
      }
    }

    if (order?.customerName) {
      const qs = await getDocs(
        query(collection(db, 'customers'), where('name', '==', order.customerName), limit(1))
      );
      if (!qs.empty) {
        const s = qs.docs[0];
        return { id: s.id, ...s.data() };
      }
    }
  } catch (e) {
    console.warn('Không lấy được khách hàng từ Firestore:', e);
  }
  return null;
}

async function attachCustomer(order) {
  if (!order) return order;
  if (order.customer && (order.customer.name || order.customer.address || order.customer.code)) {
    return order;
  }
  const customer = await fetchCustomerFromFirestore(order);
  if (!customer) return order;
  return { ...order, customer };
}

/* --- Recalc totals from selected items --- */
function recalcFinancials(order = {}) {
  const n = (v) => Number(v ?? 0);
  const items = Array.isArray(order.items) ? order.items : [];

  let subtotal = 0;
  const mapped = items.map((it) => {
    const q = n(it.quantity);
    const p = n(it.exportPrice ?? it.price ?? it.unitPrice ?? it.importPrice);
    const s = q * p;
    subtotal += s;
    return { ...it, __q: q, __p: p, __s: s };
  });

  const shippingFee     = n(order.shippingFee);
  const amountPaid      = n(order.amountPaid);
  const taxRate         = n(order.taxRate);
  let   discountRate    = n(order.discountRate);
  let   discountAmount  = n(order.discountAmount);

  if (discountRate > 0) {
    discountAmount = Math.round(subtotal * discountRate / 100);
  } else if (discountAmount > 0) {
    discountRate = subtotal ? (discountAmount / subtotal) * 100 : 0;
  }

  const taxableBase = Math.max(0, subtotal - discountAmount);
  const taxAmount   = Math.round(taxableBase * taxRate / 100);
  const grandTotal  = taxableBase + taxAmount + shippingFee;
  const remainingAmount = grandTotal - amountPaid;

  return {
    items: mapped, subtotal, discountRate, discountAmount,
    taxRate, taxAmount, shippingFee, grandTotal, amountPaid, remainingAmount,
  };
}

/* ---------------- Build data cho template ---------------- */
const buildTemplateData = (order = {}, company = {}) => {
  const soDate   = toDate(order.soDate);
  const shipDate = toDate(order.shippingDate);

  // TÍNH LẠI theo items đã chọn
  const fin = recalcFinancials(order);

  const items = (fin.items || []).map((it, i) => ({
    idx: i + 1,
    productName: it.productName || '',
    quantity: it.__q,
    exportPrice: it.__p,
    exportPriceVND: VND(it.__p),
    lineTotal: it.__s,
    lineTotalVND: VND(it.__s),
  }));

  const toUpperVI = (s) => String(s ?? '').toLocaleUpperCase('vi-VN');
  const company_representative = pick(company, ['representative','legalRepresentative'], '');
  const company_representative_upper = toUpperVI(company_representative);
  const company_bank_account   = pick(company, ['bankAccount.number','bankAccount','accountNumber','bankAccountNo'], '');

  // Tiền bằng chữ
  const subtotalText        = moneyToWordsVI(fin.subtotal);
  const taxAmountText       = moneyToWordsVI(fin.taxAmount);
  const shippingFeeText     = moneyToWordsVI(fin.shippingFee);
  const grandTotalText      = moneyToWordsVI(fin.grandTotal);
  const amountPaidText      = moneyToWordsVI(fin.amountPaid);
  const remainingAmountText = moneyToWordsVI(fin.remainingAmount);

  return {
    // SO #
    so_number: order.soNumber || '',
    so_day:    soDate ? pad2(soDate.getDate())       : '',
    so_month:  soDate ? pad2(soDate.getMonth() + 1)  : '',
    so_year:   soDate ? String(soDate.getFullYear()) : '',

    ship_day:   shipDate ? pad2(shipDate.getDate())       : '',
    ship_month: shipDate ? pad2(shipDate.getMonth() + 1)  : '',
    ship_year:  shipDate ? String(shipDate.getFullYear()) : '',

    // Company
    company_name: company.name || '',
    company_representative_upper,
    company_address: company.address || '',
    company_phone: company.phone || '',
    company_tax: company.taxCode || '',
    company_representative,
    company_bank_account,

    company: {
      name: company.name || '',
      address: company.address || '',
      phone: company.phone || '',
      taxCode: company.taxCode || '',
      representative: company_representative,
      representativeUpper: company_representative_upper,
      bankAccount: company_bank_account,
    },

    // Customer
    customer_name:          pick(order, ['customerName','customer.name'], ''),
    customer_address:       pick(order, ['customerAddress','customer.address','address'], ''),
    customer_code:          pick(order, ['customerCode','customer.code','code'], ''),
    customer_representative:pick(order, ['customerRepresentative','customer.representative'], ''),
    customer_position:      pick(order, ['customerPosition','customer.position'], ''),

    customer: {
      name:          pick(order, ['customer.name','customerName'], ''),
      address:       pick(order, ['customer.address','customerAddress','address'], ''),
      code:          pick(order, ['customer.code','customerCode','code'], ''),
      representative:pick(order, ['customer.representative','customerRepresentative'], ''),
      position:      pick(order, ['customer.position','customerPosition'], ''),
      phone:         pick(order, ['customer.phone'], ''),
      email:         pick(order, ['customer.email'], ''),
      note:          pick(order, ['customer.note'], ''),
    },

    // Table + totals (ĐÃ TÍNH LẠI)
    items,
    subtotal: fin.subtotal,             subtotalVND: VND(fin.subtotal),
    discountRate: fin.discountRate,
    discountAmount: fin.discountAmount, discountAmountVND: VND(fin.discountAmount),
    taxRate: fin.taxRate,               taxAmount: fin.taxAmount,      taxAmountVND: VND(fin.taxAmount),
    shippingFee: fin.shippingFee,       shippingFeeVND: VND(fin.shippingFee),
    grandTotal: fin.grandTotal,         grandTotalVND: VND(fin.grandTotal),
    amountPaid: fin.amountPaid,         amountPaidVND: VND(fin.amountPaid),
    remainingAmount: fin.remainingAmount, remainingAmountVND: VND(fin.remainingAmount),

    // Text amounts
    subtotalText,
    taxAmountText,
    shippingFeeText,
    grandTotalText,
    amountPaidText,
    remainingAmountText,

    // Alias
    grand_total_text: grandTotalText,
    remaining_text:   remainingAmountText,
  };
};

/* ---------------- Merge client → DOCX ---------------- */
async function buildMergedDocx(enrichedOrder, company = {}) {
  const res = await fetch(getTemplateURL());
  if (!res.ok) throw new Error('Không tìm thấy /template.docx trên Hosting');
  const arrayBuffer = await res.arrayBuffer();

  const zip = new PizZip(arrayBuffer);
  const ok = Object.keys(zip.files || {}).some((k) => /^word\/document\.xml$/i.test(k));
  if (!ok) {
    const entries = Object.keys(zip.files || {});
    throw new Error(
      `Template không hợp lệ (thiếu word/document.xml). Hãy Save As -> .docx\nZIP: ${entries.join(', ')}`
    );
  }

  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    nullGetter: () => '',
  });
  doc.setData(buildTemplateData(enrichedOrder, company));

  try { doc.render(); }
  catch (err) {
    const details = Array.isArray(err?.properties?.errors)
      ? err.properties.errors.map(e => `• ${e.properties?.explanation || e.message}`).join('\n')
      : '';
    throw new Error(`Placeholder sai/thiếu trong template.\n${details}`);
  }

  return doc.getZip().generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}

/* ================== Public APIs ================== */
export async function exportContractDocx(order, company = {}) {
  try {
    const enriched = await attachCustomer(order);
    const blob = await buildMergedDocx(enriched, company);
    const fileName = `HD_${enriched?.soNumber || 'hop-dong'}.docx`;
    saveAs(blob, fileName);
  } catch (e) {
    console.error('Xuất DOCX thất bại:', e);
    alert(`Xuất HỢP ĐỒNG (DOCX) thất bại: ${e?.message || e}`);
  }
}

export async function exportContractPdf(order, company = {}) {
  try {
    const enriched = await attachCustomer(order);
    const payload = { order: enriched, company, templateUrl: getTemplateURL() };

    const res = await fetch('/api/contract/pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || `API lỗi ${res.status}`);
    }

    const buf = await res.arrayBuffer();
    const blob = new Blob([buf], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `HD_${enriched?.soNumber || 'hop-dong'}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error('Xuất PDF thất bại:', e);
    alert(`Xuất HỢP ĐỒNG (PDF) thất bại: ${e?.message || e}`);
  }
}

export const exportContract = exportContractDocx;
